import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import './AdminFormStyles.css';

interface TikTokPixelFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  initialData?: any;
  loading?: boolean;
}

const TikTokPixelFormModal: React.FC<TikTokPixelFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    pixel_id: '',
    pixel_name: '',
    catalog_id_format: 'id',
    is_active: true,
  });
  const [errors, setErrors] = useState<any>({});

  // Populate form when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        pixel_id: initialData.pixel_id || '',
        pixel_name: initialData.pixel_name || '',
        catalog_id_format: initialData.catalog_id_format || 'id',
        is_active: initialData.is_active !== undefined ? initialData.is_active : true,
      });
    } else {
      setFormData({ pixel_id: '', pixel_name: '', catalog_id_format: 'id', is_active: true });
    }
    setErrors({});
  }, [initialData, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: any = {};
    const pixelIdTrimmed = formData.pixel_id.trim().toUpperCase();

    if (!pixelIdTrimmed) {
      newErrors.pixel_id = 'Pixel ID is required';
    } else if (!/^[A-Z0-9]+$/.test(pixelIdTrimmed)) {
      newErrors.pixel_id = 'Pixel ID must contain only letters and numbers (e.g., C3AB1234567890)';
    } else if (pixelIdTrimmed.length < 10 || pixelIdTrimmed.length > 20) {
      newErrors.pixel_id = 'Pixel ID must be between 10 and 20 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        ...formData,
        pixel_id: formData.pixel_id.trim().toUpperCase(),
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Edit TikTok Pixel' : 'Add TikTok Pixel'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="admin-form-modal">
        <div className="admin-form-table">

          {/* Pixel ID */}
          <div className="admin-form-row">
            <div className="admin-form-label">Pixel ID *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="pixel_id"
                value={formData.pixel_id}
                onChange={handleChange}
                placeholder="e.g., C3AB1234567890"
                className={errors.pixel_id ? 'has-error' : ''}
                disabled={loading}
                autoComplete="off"
              />
              {errors.pixel_id && <span className="error-text">{errors.pixel_id}</span>}
              <span className="admin-form-hint">
                Found in TikTok Ads Manager → Assets → Events → Web Events
              </span>
            </div>
          </div>

          {/* Pixel Name */}
          <div className="admin-form-row">
            <div className="admin-form-label">Pixel Name</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="pixel_name"
                value={formData.pixel_name}
                onChange={handleChange}
                placeholder="e.g., Main Website Pixel (optional)"
                disabled={loading}
              />
              <span className="admin-form-hint">Optional label for your reference</span>
            </div>
          </div>

          {/* Catalog Match Identifier */}
          <div className="admin-form-row">
            <div className="admin-form-label">Catalog Identifier Format</div>
            <div className="admin-form-input">
              <select
                name="catalog_id_format"
                value={formData.catalog_id_format}
                onChange={handleChange}
                disabled={loading}
                className="admin-form-select"
                style={{ width: '100%', padding: '8px 10px', borderRadius: '4px', border: '1px solid #d1d5db' }}
              >
                <option value="id">Product Database ID (Numeric: 1, 2, 3...) — Default &amp; Recommended</option>
                <option value="sku">Product SKU Code (e.g. STR-DNIM, CTN-TSHIRT) — If TikTok Catalog uses SKUs</option>
              </select>
              <span className="admin-form-hint">
                Controls what value is sent as <code>content_id</code> so it matches products in your TikTok Catalog with 100% accuracy.
              </span>
            </div>
          </div>

          {/* Active Status */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Status</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>Active Pixel</span>
              </label>
              <span className="admin-form-hint">Enable TikTok tracking on your website</span>
            </div>
          </div>

        </div>

        <div className="admin-form-actions">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading}>
            {loading ? 'Saving...' : initialData ? 'Update Pixel' : 'Add Pixel'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TikTokPixelFormModal;
